import React, { useState, useContext } from "react";
import { useNavigate } from "react-router-dom";
import { callFunction } from "./api/callFunction";
import imageCompression from "browser-image-compression";
import { Input } from "./components/Input";
import { PhotoUploadInput } from "./components/PhotoUploadInput";
import { PrimaryButton, SecondaryButton } from "./components/Button";
import { GlobalContext } from "./context/GlobalContext";
import UserAPI from "./api/UserApi";
import VendorAPI from "./api/VendorApi";
import { showToast } from "./utils/showToast";
import { capture, EVENTS } from "./analytics";

const COMPRESSION_OPTIONS = {
  maxSizeMB: 1,
  maxWidthOrHeight: 1024,
  useWebWorker: true,
};

const MEETUP_ID_MIN_LENGTH = 6;
const MEETUP_ID_MAX_LENGTH = 12;

// Our events are published to this Meetup group, so every host has to be a member
// of it. New hosts arrive here straight from the approval text and have usually
// never joined, so onboarding walks them through it before asking for the ID.
const MEETUP_GROUP_URL = "https://www.meetup.com/gist-irl/";

export default function ProfileSetup() {
  const navigate = useNavigate();
  const { globalState, setGlobalState } = useContext(GlobalContext);
  const [fullName, setFullName] = useState(globalState?.user?.name || "");
  const [meetupMemberID, setMeetupMemberID] = useState(globalState?.vendor?.meetupMemberID || "");
  const [photo, setPhoto] = useState({ file: null, preview: globalState?.user?.photoURL || "" });
  const [vendorBio, setVendorBio] = useState(globalState?.vendor?.bio || "");
  const [currentStep, setCurrentStep] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [stripeStarted, setStripeStarted] = useState(false);

  const handlePhotoChange = (file, preview) => {
    setPhoto({ file, preview });
  };

  // Open Stripe payout onboarding in a new tab. Same on-demand path Settings
  // uses: stripeConnectAccountLink creates (or reuses) the host's Express account
  // and returns a fresh hosted-onboarding link. Optional here — the host can also
  // do this later from Settings, so a failure just toasts and doesn't block finish.
  const startStripeOnboarding = async () => {
    // Open the tab synchronously (inside the click) so pop-up blockers allow it.
    const newWindow = window.open("", "_blank");
    if (newWindow) {
      newWindow.document.body.innerHTML =
        '<div style="font-family: Arial, sans-serif; text-align: center; padding-top: 50px; color: #333;">Connecting to Stripe…</div>';
      newWindow.document.title = "Loading…";
    }
    try {
      const result = await callFunction("stripeConnectAccountLink");
      const link = result.accountLinkUrl;
      if (newWindow) {
        newWindow.location.href = link;
        setStripeStarted(true);
      } else {
        showToast("Please allow pop-ups to connect your Stripe account.", "error");
      }
    } catch (error) {
      if (newWindow) newWindow.close();
      console.error("Error opening Stripe onboarding:", error);
      showToast("Couldn't open Stripe. You can set up payouts later in Settings.", "error");
    }
  };

  const handleNext = (e) => {
    e.preventDefault();

    const existingPhotoURL = globalState?.user?.photoURL || "";
    if (!photo.file && !existingPhotoURL) {
      showToast("Please upload a profile photo", "error");
      return;
    }
    if (fullName.trim().length < 2) {
      showToast("Please enter your name with at least 2 characters", "error");
      return;
    }
    if (meetupMemberID.length < MEETUP_ID_MIN_LENGTH || meetupMemberID.length > MEETUP_ID_MAX_LENGTH) {
      showToast(`Meetup Member ID must be ${MEETUP_ID_MIN_LENGTH}–${MEETUP_ID_MAX_LENGTH} digits`, "error");
      return;
    }

    setCurrentStep(2);
  };

  const handleFinish = async ({ includeBio }) => {
    setIsSubmitting(true);

    try {
      const userID = globalState.user.userID;
      const trimmedName = fullName.trim();

      const photoURL = photo.file
        ? await UserAPI.uploadUserPhoto(userID, await imageCompression(photo.file, COMPRESSION_OPTIONS))
        : globalState.user.photoURL;

      const updatedUser = await UserAPI.updateUserProfile(userID, trimmedName, photoURL);

      const trimmedMeetupID = meetupMemberID.trim();
      let updatedVendor = await VendorAPI.updateVendor(globalState.vendorID, "meetupMemberID", trimmedMeetupID);

      const trimmedBio = vendorBio.trim();
      if (includeBio && trimmedBio) {
        updatedVendor = await VendorAPI.updateVendor(globalState.vendorID, "bio", trimmedBio);
      }

      setGlobalState((prevState) => ({
        ...prevState,
        user: updatedUser,
        vendor: { ...prevState.vendor, ...updatedVendor },
        needsProfileSetup: false,
      }));

      // Deliberately NOT `profile_completed`. That is a member-funnel event with
      // a fixed meaning on pwa/ios (taxonomy §6 row 6), and a host finishing the
      // panel's onboarding is a different act by a different population — firing
      // it here would quietly inflate member activation with operator onboarding,
      // which is the same mistake decision 4 rejected for `otp_verified`.
      capture(EVENTS.HOST_PROFILE_SAVED, {
        context: "profile_setup",
        changedPhoto: !!photo.file,
        includedBio: includeBio && !!vendorBio.trim(),
      });
      navigate("/home");
    } catch (error) {
      console.error("Error setting up profile:", error);
      showToast("Failed to save profile. Please try again.", "error");
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!globalState.isAuthenticated || globalState.loading) {
    return null;
  }

  return (
    <div className="flex min-h-screen items-center justify-center">
      <div className="w-full max-w-md px-6 py-12">
        <div className="sm:mx-auto sm:w-full sm:max-w-sm">
          <img
            className="mx-auto h-24 w-auto rounded-lg"
            src="/gist_logo.jpg"
            alt="gist"
          />
          <h2 className="mt-6 text-center text-2xl font-bold leading-9 tracking-tight text-gray-900 dark:text-gray-100">
            {currentStep === 1 ? "Set up your profile" : "A couple of optional extras"}
          </h2>
          <p className="mt-2 text-center text-sm text-gray-500 dark:text-gray-400">
            {currentStep === 1
              ? "Add your name and photo, and join our Meetup group to get started"
              : "Add a bio and set up payouts now, or come back to these later in Settings"}
          </p>
        </div>

        {currentStep === 1 ? (
          <form onSubmit={handleNext} className="mt-8 space-y-6">
            <PhotoUploadInput
              label="Profile Photo"
              preview={photo.preview}
              onChange={handlePhotoChange}
            />

            <div>
              <Input
                label="Full name"
                type="text"
                value={fullName}
                onChange={(e) => setFullName(e.target.value)}
                required
              />
            </div>

            <div className="rounded-md bg-gray-50 dark:bg-gray-800/60 p-4 ring-1 ring-inset ring-gray-200 dark:ring-gray-700">
              <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                Join the Gist IRL Meetup group
              </h3>
              <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                We publish your events to Meetup, so you need to be a member of the group before you can
                host. Join it, then come back here and paste your member ID.
              </p>
              <a
                href={MEETUP_GROUP_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-3 inline-flex items-center gap-1 text-sm font-medium text-primary hover:underline"
              >
                Open the Meetup group
                <span aria-hidden="true">&rarr;</span>
              </a>

              <div className="mt-4">
                <Input
                  label="Meetup Member ID"
                  type="text"
                  inputMode="numeric"
                  placeholder="123456789"
                  maxLength={MEETUP_ID_MAX_LENGTH}
                  value={meetupMemberID}
                  onChange={(e) => setMeetupMemberID(e.target.value.replace(/\D/g, ""))}
                />
                <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                  Once you have joined, open your Meetup profile — the number in the profile URL is your
                  member ID, e.g.{" "}
                  <span className="font-mono">
                    meetup.com/members/<span className="font-semibold text-gray-900 dark:text-gray-100">123456789</span>/
                  </span>{" "}
                  It is usually 9 digits.
                </p>
              </div>
            </div>

            <PrimaryButton type="submit">Next</PrimaryButton>
          </form>
        ) : (
          <div className="mt-8 space-y-6">
            <div>
              <label
                htmlFor="vendor-bio"
                className="block text-sm font-medium leading-6 text-gray-500 dark:text-gray-400"
              >
                Bio
              </label>
              <div className="mt-1">
                <textarea
                  id="vendor-bio"
                  name="vendor-bio"
                  rows={5}
                  value={vendorBio}
                  onChange={(e) => setVendorBio(e.target.value)}
                  placeholder="A short intro shown to attendees on your event pages. Why you host, what your nights feel like, what people can expect."
                  className="px-4 block w-full rounded-md border-0 py-1.5 text-gray-900 dark:text-gray-100 dark:bg-gray-800 shadow-sm ring-1 ring-inset ring-gray-300 dark:ring-gray-600 placeholder:text-gray-400 dark:placeholder:text-gray-500 focus:ring-2 focus:ring-inset focus:ring-primary text-sm sm:text-base sm:leading-6 resize-y"
                />
              </div>
              <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                Hosts with a bio fill their seats faster. You can always edit this later in Settings.
              </p>
            </div>

            <div className="rounded-md bg-gray-50 dark:bg-gray-800/60 p-4 ring-1 ring-inset ring-gray-200 dark:ring-gray-700">
              <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                Set up payouts
              </h3>
              <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                Connect Stripe so you get paid for ticket sales. This opens in a new tab — you can do it now
                or later in Settings.
              </p>
              <button
                type="button"
                onClick={startStripeOnboarding}
                className="mt-3 inline-flex items-center gap-1 rounded-md px-3 py-1.5 text-sm font-medium text-primary ring-1 ring-inset ring-primary hover:bg-primary hover:text-white transition-colors"
              >
                Set up payouts with Stripe
                <span aria-hidden="true">&rarr;</span>
              </button>
              {stripeStarted && (
                <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
                  Opened in a new tab. Finish there, then come back and save — you can revisit payouts anytime
                  in Settings.
                </p>
              )}
            </div>

            <PrimaryButton
              type="button"
              onClick={() => !isSubmitting && handleFinish({ includeBio: true })}
            >
              {isSubmitting ? "Saving..." : "Save and finish"}
            </PrimaryButton>
            <SecondaryButton
              type="button"
              onClick={() => !isSubmitting && handleFinish({ includeBio: false })}
            >
              Skip for now
            </SecondaryButton>
          </div>
        )}
      </div>
    </div>
  );
}
